/**
 * taxonomy — r7.6.4 "Taxonomy"
 *
 *   The category architecture (13 categories × 103 sub-categories, each category with a flower, tagline and colour)
 *   is data: `GET /api/categories`. This module is the one place the web reads it.
 *
 *   taxonomy.list()              cached tree (60 s, shared by every caller; fail-open to FALLBACK_CATEGORIES)
 *   taxonomy.get(slug)           one category
 *   useCategories()              hook: { categories, loading, error, bySlug }
 *   catColour(cat, mode)         the category's colour for the current mode ('dark' | 'light')
 *   applyCategoryVars(cats, mode) sets --snt-cat-<slug> on <html> so CSS can paint by slug
 *   FALLBACK_CATEGORIES          the seed's slugs / names / colours — lets the shell render before the API answers
 *   admin.*                      console calls (taxonomy.manage)
 */
import { useEffect, useState } from 'react';
import { api } from './api';

export const FALLBACK_CATEGORIES = [
  { slug: 'music',                 name: 'Music',                   flower: 'Rose',        colour: '#F43F5E', colour_light: '#BE123C', icon: 'Music' },
  { slug: 'comedy-theatre',        name: 'Comedy & Theatre',        flower: 'Daisy',       colour: '#FDBA74', colour_light: '#C2410C', icon: 'Drama' },
  { slug: 'festivals-exhibitions', name: 'Festivals & Exhibitions', flower: 'Sunflower',   colour: '#FACC15', colour_light: '#A16207', icon: 'Sparkles' },
  { slug: 'nightlife-parties',     name: 'Nightlife & Parties',     flower: 'Orchid',      colour: '#60A5FA', colour_light: '#1D4ED8', icon: 'Moon' },
  { slug: 'workshops-conferences', name: 'Workshops & Conferences', flower: 'Lotus',       colour: '#EC4899', colour_light: '#BE185D', icon: 'GraduationCap' },
  { slug: 'family-kids',           name: 'Family & Kids',           flower: 'Tulip',       colour: '#FB7185', colour_light: '#B91C1C', icon: 'Baby' },
  { slug: 'virtual-hybrid',        name: 'Virtual & Hybrid',        flower: 'Lavender',    colour: '#A78BFA', colour_light: '#6D28D9', icon: 'Wifi' },
  { slug: 'corporate',             name: 'Corporate Events',        flower: 'Lily',        colour: '#F9A8D4', colour_light: '#9D174D', icon: 'Briefcase' },
  { slug: 'small-coffee',          name: 'Small & Coffee Events',   flower: 'Jasmine',     colour: '#5EEAD4', colour_light: '#0F766E', icon: 'Coffee' },
  { slug: 'cultural',              name: 'Cultural Events',         flower: 'Marigold',    colour: '#F59E0B', colour_light: '#B45309', icon: 'Flower' },
  { slug: 'city-specific',         name: 'City-specific Events',    flower: 'Hibiscus',    colour: '#DC2626', colour_light: '#991B1B', icon: 'MapPin' },
  { slug: 'sports',                name: 'Sports',                  flower: 'Lotus bud',   colour: '#34D399', colour_light: '#047857', icon: 'Trophy' },
  { slug: 'movies',                name: 'Movies',                  flower: 'Jasmine bud', colour: '#CBD5E1', colour_light: '#334155', icon: 'Film' },
].map((c, i) => ({ ...c, id: null, tagline: null, art_url: null, sort_order: (i + 1) * 10, event_count: 0, sub_categories: [] }));

export const FLOWER_GLYPH = {
  music: '❀', 'comedy-theatre': '✿', 'festivals-exhibitions': '❁', 'nightlife-parties': '✾', 'workshops-conferences': '❃',
  'family-kids': '✽', 'virtual-hybrid': '❇', corporate: '❋', 'small-coffee': '✼', cultural: '✺', 'city-specific': '❉', sports: '◯', movies: '▶',
};

const TTL_MS = 60000;
let CACHE = null;
let CACHE_AT = 0;
let INFLIGHT = null;
const listeners = new Set();

function notify() { for (const fn of listeners) fn(CACHE); }

export const taxonomy = {
  async list({ force = false } = {}) {
    if (!force && CACHE && Date.now() - CACHE_AT < TTL_MS) return CACHE;
    if (INFLIGHT) return INFLIGHT;
    INFLIGHT = api.get('/api/categories')
      .then((r) => {
        const list = Array.isArray(r?.categories) && r.categories.length ? r.categories : FALLBACK_CATEGORIES;
        CACHE = list; CACHE_AT = Date.now(); notify();
        return list;
      })
      .catch(() => { CACHE = CACHE || FALLBACK_CATEGORIES; CACHE_AT = Date.now(); notify(); return CACHE; })
      .finally(() => { INFLIGHT = null; });
    return INFLIGHT;
  },
  async get(slug) {
    const r = await api.get(`/api/categories/${encodeURIComponent(slug)}`);
    return r.category;
  },
  invalidate() { CACHE = null; CACHE_AT = 0; },
  cached() { return CACHE; },
};

export const admin = {
  list: () => api.get('/api/admin/categories').then((r) => r.categories || []),
  create: (body) => api.post('/api/admin/categories', body).then((r) => r.category),
  update: (ref, body) => api.patch(`/api/admin/categories/${encodeURIComponent(ref)}`, body).then((r) => r.category),
  deactivate: (ref) => api.delete(`/api/admin/categories/${encodeURIComponent(ref)}`),
  createSub: (ref, body) => api.post(`/api/admin/categories/${encodeURIComponent(ref)}/sub-categories`, body).then((r) => r.sub_category),
  updateSub: (ref, body) => api.patch(`/api/admin/sub-categories/${encodeURIComponent(ref)}`, body).then((r) => r.sub_category),
  reorder: (kind, ids) => api.post('/api/admin/taxonomy/reorder', { kind, ids }),
};

export function bySlugMap(list) {
  return Object.fromEntries((list || []).map((c) => [c.slug, c]));
}

export function catColour(cat, mode = 'dark') {
  if (!cat) return 'var(--snt-accent)';
  return mode === 'light' ? (cat.colour_light || cat.colour) : (cat.colour || cat.colour_light);
}

/** --snt-cat-<slug> for every category, so cards / chips paint by slug without prop drilling. */
export function applyCategoryVars(list, mode = 'dark') {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  for (const c of list || []) root.style.setProperty(`--snt-cat-${c.slug}`, catColour(c, mode));
}

/** Resolve the colour for an event row (list rows carry category_slug + category_colour from the server). */
export function eventCategoryColour(event, list, mode = 'dark') {
  if (!event) return null;
  if (mode === 'light' && event.category_colour_light) return event.category_colour_light;
  if (mode !== 'light' && event.category_colour) return event.category_colour;
  const cat = event.category_slug ? (list || []).find((c) => c.slug === event.category_slug) : null;
  return cat ? catColour(cat, mode) : null;
}

export function useCategories() {
  const [categories, setCategories] = useState(() => CACHE || FALLBACK_CATEGORIES);
  const [loading, setLoading] = useState(!CACHE);
  const [error, setError] = useState(null);
  useEffect(() => {
    let alive = true;
    const onChange = (list) => { if (alive) { setCategories(list); setLoading(false); } };
    listeners.add(onChange);
    taxonomy.list().then(onChange).catch((e) => { if (alive) { setError(e); setLoading(false); } });
    return () => { alive = false; listeners.delete(onChange); };
  }, []);
  return { categories, loading, error, bySlug: bySlugMap(categories) };
}

/** Wizard helper: which sub-categories may pair with a category slug. */
export function subsFor(list, slug) {
  const c = (list || []).find((x) => x.slug === slug);
  return c ? c.sub_categories || [] : [];
}

/** Legacy → slug (r7.0 wizard values still stored on older drafts). */
export const LEGACY_TO_SLUG = {
  music: 'music', comedy: 'comedy-theatre', theatre: 'comedy-theatre', devotional: 'cultural', dance: 'cultural',
  workshop: 'workshops-conferences', conference: 'workshops-conferences', sports: 'sports', kids: 'family-kids',
  food: 'city-specific', exhibition: 'festivals-exhibitions', general: '',
};

export function slugForLegacy(text, list) {
  if (!text) return '';
  const t = String(text).trim().toLowerCase();
  const hit = (list || []).find((c) => c.slug === t || c.name.toLowerCase() === t);
  if (hit) return hit.slug;
  return LEGACY_TO_SLUG[t] ?? '';
}
